import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { environment } from '@bling-fe/shared/env';
import { ModalService } from '@bling-fe/shared/modals/base-modal';
import {
  APP_TENANT_CONFIG,
  SHARED_WEB_HOSTS,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import { Tenant } from '../../core/declarations/tenant.interface';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import {
  ShareKind,
  ShareModalComponent
} from '../../modals/share-modal/share-modal.component';

type WebLocation = Pick<Location, 'hostname' | 'origin'>;

export const appOrigin = (
  tenant: Tenant,
  configs: TenantConfig[],
  webLocation: WebLocation = window.location
): string => {
  const config = configs.find(c => c.subdomain === tenant.subdomain);
  const hostNamesCooperative =
    !config || !SHARED_WEB_HOSTS.includes(webLocation.hostname);
  if (!Capacitor.isNativePlatform() && hostNamesCooperative) {
    return webLocation.origin;
  }

  const host = environment.production
    ? config?.urls.production[0]
    : config?.urls.staging[0];
  if (host) return `https://${host}`;

  return new URL(tenant.base_url).origin;
};

@Injectable({ providedIn: 'root' })
export class AppLinkService {
  private readonly currentTenant = toSignal(
    this.store.select(getCurrentTenant)
  );

  constructor(
    private store: Store,
    @Inject(APP_TENANT_CONFIG) private tenantConfigs: TenantConfig[],
    private modalService: ModalService
  ) {}

  url(path: string): string {
    const tenant = this.currentTenant();
    const origin =
      tenant && tenant !== 'loading'
        ? appOrigin(tenant, this.tenantConfigs)
        : window.location.origin;
    return `${origin}${path}`;
  }

  /** The sheet with the link and its QR code. */
  share(title: string, path: string, kind: ShareKind): Promise<void> {
    return this.modalService.presentAndGetResult(ShareModalComponent, {
      title,
      url: this.url(path),
      kind
    });
  }
}
